import Configuration from "config";
import moment from "moment-timezone";
import { API_BASE_URL } from "services/restUtil";
import { MIN_CONVERSATION_START_CREDITS } from "services/talkToTally/useTallyConversation";
import { getSessionUser } from "./auth";
import { User, UserProfile, UserRole } from "./model";

export function getUserFullName(
  profile: { first_name?: string | null; last_name?: string | null } | null | undefined
) {
  if (!profile) return "";
  return [profile.first_name, profile.last_name].filter(Boolean).join(" ");
}

export function getUserFirstName(
  profile: { first_name?: string | null } | null | undefined
) {
  return profile?.first_name ?? "";
}

export type OnlineStatus = "offline" | "away" | "online";

const ONLINE_THRESHOLD = 5 * 60 * 1000; // 5 min
const AWAY_THRESHOLD = 60 * 60 * 1000; // 1 hour

export function getStatusByLastSeen(lastSeen: string | Date): OnlineStatus {
  const diff = moment.utc().diff(moment.utc(lastSeen));
  if (diff < ONLINE_THRESHOLD) return "online";
  if (diff < AWAY_THRESHOLD) return "away";
  return "offline";
}

export const isPending = (status: string) => status === "pending";
export const isSuspended = (status: string) => status === "suspended";
export const isActive = (status: string) => status === "active";
export const isDeleted = (status: string) => status === "deleted";

// The role predicates read nothing but `roles`, so they serve both the session user
// and the admin user model rather than forcing a conversion between them.
type RoleBearer = { roles: UserRole[] };

export const isUserTeacher = (user?: RoleBearer | null) => hasRole("instructor", user);
export const isUserStudent = (user?: RoleBearer | null) => hasRole("student", user);
export const isUserAdmin = (user?: RoleBearer | null) => hasRole("admin", user);
export const isUserTeamManager = (user?: RoleBearer | null) =>
  hasRole("team-manager", user);
export const isUserCustomerService = (user?: RoleBearer | null) =>
  hasRole("customer-service", user);
export const isUserCompanyManager = (user?: UserProfile | null) =>
  hasRole("company-manager", user);
export function isUserOneOnOneStudent(user?: User | null): boolean {
  return hasRole("one-on-one-student", user);
}

export function hasRole(
  role: UserRole,
  user: RoleBearer | null | undefined = getSessionUser()
) {
  return user?.roles.includes(role) === true;
}

export function isSelfStudyUser(user = getSessionUser()) {
  return user?.student?.type === "self-study";
}
export function isOneOnOneUser(user = getSessionUser()) {
  return user?.student?.type === "one-on-one";
}

export function isUserContentAdmin(user?: UserProfile): boolean {
  // TODO uncomment when a new role will be added.
  // return hasRole("content-admin", user);
  if (Configuration.CONTENT_ADMINS.length === 0) {
    return false;
  }

  const resolvedUser = user ?? getSessionUser();
  if (!resolvedUser) {
    return false;
  }
  return Configuration.CONTENT_ADMINS.includes(resolvedUser.id);
}

// Enhances the user object with convenience properties.
export const enhanceUser = (user: User) => {
  const { student } = user;

  const subscriptionAiCredits = user.student?.subscription_plan?.ai_tokens ?? 0;
  const aiCreditBalance = student?.credits.ai_credits ?? 0;
  const usedAiCredits = student?.credits.ai_credits_used ?? 0;
  const canStartTallyConversation = aiCreditBalance >= MIN_CONVERSATION_START_CREDITS;

  const isTeamManager = hasRole("team-manager", user);
  const isCompanyManager = hasRole("company-manager", user);
  const isManager = isTeamManager || isCompanyManager;
  const isPurchaseRequired = !!student && !student.has_pro_access;

  return {
    ...user,

    // Student type properties
    isOneOnOne: student?.type === "one-on-one",
    isGroupStudent: student?.type === "group",
    isSelfStudy: student?.type === "self-study",

    // Role-based properties
    isTeacher: hasRole("instructor", user),
    isStudent: hasRole("student", user),
    isAdmin: hasRole("admin", user),
    isCustomerService: hasRole("customer-service", user),
    isTeamManager: isTeamManager,
    isCompanyManager: isCompanyManager,
    isManager,
    isContentAdmin: isUserContentAdmin(user),

    // In practice a user can be a member of only one team.
    team: user.company?.teams[0],

    isPurchaseRequired,
    isOnboardingRequired:
      student && !student.has_all_onboarding_data && !isManager && !isPurchaseRequired,

    canPurchase:
      !!student &&
      student.type !== "group" &&
      (student.can_purchase_lessons || !user.company),

    // Feature access
    canAccessSpeakingClub:
      student?.access.speaking_club === true && user.features.workshops,
    canAccessTally:
      canStartTallyConversation || subscriptionAiCredits > 0 || usedAiCredits > 0,
    canAccessAiExercises: aiCreditBalance > 0,
    canStartTallyConversation,
    canUseDictionaryTools:
      student?.access.dictionary === true || hasRole("instructor", user),
  };
};

export type EnhancedUser = ReturnType<typeof enhanceUser>;

export const getUserAvatarUrl = (userId: string) =>
  `${API_BASE_URL}/user/${userId}/profile/avatar`;

export const getOTPQRCodeUrl = () => `${API_BASE_URL}/user/totp`;
